/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { type ChangeEvent } from 'react'

import type { EuiStepInterface } from '@elastic/eui/src/components/steps/step'

import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { ScopeType, type Budget, type BudgetConfigurationValues } from '../../types'

import Alerts from './steps/Alerts'
import Configuration from './steps/Configuration'
import Scope from './steps/Scope/Scope'

const defaultValues = {
  name: '',
  amount: '' as unknown as number,
  scopeType: undefined,
  scopeValues: undefined,
}

type useBudgetSettingsProps = {
  alerts: Array<Pick<Budget['alerts'][number], 'threshold' | 'threshold_type'>>
  initialValues?: BudgetConfigurationValues
  isLoading: boolean
  isSubmitting: boolean
  onSubmit: (values: BudgetConfigurationValues) => void
  amountRef: React.RefObject<HTMLInputElement>
  cloudResourceRef: React.RefObject<HTMLDivElement>
  nameRef: React.RefObject<HTMLInputElement>
  scopeRef: React.RefObject<HTMLDivElement>
}

const useBudgetSettings = ({
  alerts,
  initialValues,
  isLoading,
  isSubmitting,
  onSubmit,
  amountRef,
  cloudResourceRef,
  nameRef,
  scopeRef,
}: useBudgetSettingsProps) => {
  const { formatMessage } = useIntl()
  const organizationId = useOrganizationId()

  const _validateFields = (values: BudgetConfigurationValues) => {
    const errors: Partial<Record<keyof BudgetConfigurationValues, string>> = {}

    const { name, amount, scopeType, scopeValues } = values

    // scope error validations
    if (!scopeType) {
      errors.scopeType = formatMessage({
        id: 'billing.notifications.budgets.configuration.scope.empty-error',
        defaultMessage: 'Select a scope option (Organization or Cloud resource)',
      })
      // the early return prevents showing multiple errors when scope is not selected
      return errors
    }

    // cloud resources error validations
    if (!scopeValues?.length) {
      errors.scopeValues = formatMessage({
        id: 'billing.notifications.budgets.configuration.scope.cloud-resources.empty-error',
        defaultMessage: 'Select a cloud resource',
      })
    }

    // name error validations
    if (!name?.length) {
      errors.name = formatMessage({
        id: 'billing.notifications.budgets.configuration.budget-name.empty-error',
        defaultMessage: 'Enter a budget name',
      })
    }

    // amount error validations
    if (!amount.toString().length || amount <= 0) {
      errors.amount = formatMessage({
        id: 'billing.notifications.budgets.configuration.target-amount.empty-error',
        defaultMessage: 'Enter a target amount',
      })
    }

    return errors
  }

  const formik = useFormik<BudgetConfigurationValues>({
    initialValues: {
      name: initialValues?.name || defaultValues.name,
      amount: initialValues?.amount || defaultValues.amount,
      scopeType: initialValues?.scopeType || defaultValues.scopeType,
      scopeValues: initialValues?.scopeValues || defaultValues.scopeValues,
    },
    validate: _validateFields,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  })

  const _handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target
    let fieldName = name

    const isChangingScopeTypeField = [ScopeType.CloudResource, ScopeType.Organization].includes(
      value as ScopeType,
    )

    if (isChangingScopeTypeField) {
      // when changing scope type we need to update two fields: scopeType and scopeValues
      fieldName = 'scopeType'

      const newScopeValues = value === ScopeType.Organization ? [organizationId] : []
      formik.setFieldValue('scopeValues', newScopeValues)
    }

    formik.setFieldValue(fieldName, value)

    // clean errors on the field that is being updated
    if (formik.errors[fieldName as keyof BudgetConfigurationValues]) {
      formik.setFieldError(fieldName, undefined)
    }
  }

  const isDisabled = !formik.values.scopeType
  const isProcessing = isLoading || isSubmitting

  const steps: EuiStepInterface[] = [
    {
      title: formatMessage({
        id: 'billing.notifications.budgets.budget-settings.scope.title',
        defaultMessage: 'Scope',
      }),
      children: (
        <Scope
          cloudResourceRef={cloudResourceRef}
          errors={formik.errors}
          handleChange={_handleChange}
          isProcessing={isProcessing}
          scopeRef={scopeRef}
          values={formik.values}
        />
      ),
      step: 1,
      ...(isLoading && { status: 'loading' }),
    },
    {
      title: formatMessage({
        id: 'billing.notifications.budgets.budget-settings.configuration.title',
        defaultMessage: 'Configuration',
      }),
      children: (
        <Configuration
          amountRef={amountRef}
          errors={formik.errors}
          handleChange={_handleChange}
          isHidden={isDisabled}
          isProcessing={isLoading || isSubmitting}
          nameRef={nameRef}
          values={formik.values}
        />
      ),
      step: 2,
      ...(isLoading && { status: 'loading' }),
    },
    {
      title: formatMessage({
        id: 'billing.notifications.budgets.budget-settings.alerts.title',
        defaultMessage: 'Alerts',
      }),
      children: <Alerts alerts={alerts} isHidden={isDisabled} />,
      step: 3,
      ...(isLoading && { status: 'loading' }),
    },
  ]

  return { ...formik, steps }
}

export default useBudgetSettings
